import { Component, Inject, OnInit, ViewChild, TemplateRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { AuthService, MultilingualService, TokenService } from '@core/services';
import { AppConstants } from '@root/app/app.constants';
import {
  AppCenterAnalytics,
  AppCenterAnalyticsAPIMsgs,
  AppInsightsService,
  MemberService,
  UserService,
} from '@shared/services';
import { emailPattern } from '@shared/utils';

@Component({
  selector: 'app-update-profile',
  templateUrl: './update-profile.component.html',
  styleUrls: ['./update-profile.component.scss'],
  standalone: false,
})
export class UpdateProfileComponent implements OnInit, OnDestroy {
  userForm: UntypedFormGroup;
  isError = false;
  isLoading = false;
  private readonly subscription = new Subscription();
  @ViewChild('success', { read: TemplateRef }) successTemplate!: TemplateRef<any>;
  @ViewChild('error', { read: TemplateRef }) errorTemplate!: TemplateRef<any>;
  emailUpdate = false;
  successMsg!: string;
  errorMsg!: string;
  otpVerified = true;
  resendOtp = false;
  otpValue = '';
  showError = false;
  mobileNumber = '';
  userEligibilityStatus: string | undefined;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly memberService: MemberService,
    private readonly authService: AuthService,
    private readonly appInsightsService: AppInsightsService,
    public readonly userService: UserService,
    private readonly tokenService: TokenService,
    public readonly multilingualService: MultilingualService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<UpdateProfileComponent>,
    private readonly snackBar: MatSnackBar
  ) {
    if (this.userService.memberProfile) {
      const userRxGrp =
        this.userService.eligibilityVerificationResponse?.clientId ===
        AppConstants.EXTERNAL_CLIENT_IDS_MAPPING.CLIENT_ID
          ? AppConstants.EXTERNAL_CLIENT_IDS_MAPPING.EXTERNAL_CLIENT_ID
          : this.userService.eligibilityVerificationResponse?.clientId;

      this.userForm = this.fb.group({
        email: [
          { value: this.userService.memberProfile.email, disabled: this.userService.isMemberTermed() },
          [Validators.required, Validators.pattern(emailPattern)],
        ],
        firstName: [this.userService.eligibilityVerificationResponse?.firstName, [Validators.required]],
        lastName: [this.userService.eligibilityVerificationResponse?.lastName, [Validators.required]],
        contact: [
          { value: this.mobileNumber, disabled: this.userService.isMemberTermed() },
          [Validators.required, Validators.minLength(10), Validators.maxLength(10)],
        ],
        dob: [
          { value: this.userService.eligibilityVerificationResponse?.birthdate, disabled: true },
          [Validators.required],
        ],
        cardId: [
          { value: this.userService.memberProfile.cardId, disabled: true },
          [Validators.required],
        ],
        rxGrp: [{ value: userRxGrp, disabled: true }, [Validators.required]],
      });
      this.userForm.controls.email.markAsTouched();
      this.userForm.controls.contact.markAsTouched();
    } else {
      this.showError = true;
      this.userForm = this.fb.group({});
    }
  }

  ngOnInit(): void {
    this.data
      ? this.appInsightsService.trackPageView(AppCenterAnalytics.ACCOUNT_PROFILE_VIEWED)
      : this.appInsightsService.trackPageView(AppCenterAnalytics.UPDATE_PROFILE_VIEWED);
    this.getContactInfo();
    this.userEligibilityStatus = this.userService.eligibilityVerificationResponse?.eligibilityStatus;
  }

  getContactInfo(): void {
    const contactNumber = this.userService.memberProfile?.contactNumber || '';
    if (contactNumber) {
      this.mobileNumber = `${contactNumber}`;
      this.userForm.patchValue({
        contact: contactNumber,
      });
    }
  }

  update(): void {
    this.isError = true;
    this.isLoading = true;

    let responseText = AppCenterAnalyticsAPIMsgs.COMMON_INITIAL_MSG;
    this.memberService
      .updateMemberProfile(
        {
          email: this.userForm.get('email')?.value || '',
          contactNumber: this.userForm.get('contact')?.value || '',
        },
        this.authService.isAuthorizedBySSO
      )
      .subscribe({
        next: () => {
          if (this.authService.isAuthorizedBySSO) {
            this.tokenService.setSSOEmail(this.userForm.get('email')?.value || '');
          }
          responseText = AppCenterAnalyticsAPIMsgs.SUCCESS_UPDATE_PROFILE;
          this.appInsightsService.trackPageView(
            `${AppCenterAnalytics.ACCOUNT_PROFILE_UPDATE_API_SUCCESS}`,
            { data: this.userForm.value, Response: responseText }
          );
          this.isLoading = false;
          this.dialogRef.close({ dialogStatus: 0 });
          this.router.navigate(['/main']);
        },
        error: (error) => {
          this.isError = true;
          this.isLoading = false;
          this.errorMsg = error;
          responseText = error ? error : AppCenterAnalyticsAPIMsgs.ERROR_UPDATE_PROFILE;
          this.snackBar.openFromTemplate(this.errorTemplate, {
            duration: 4000,
            verticalPosition: 'top',
            horizontalPosition: 'center',
          });
          this.appInsightsService.trackPageView(
            `${AppCenterAnalytics.ACCOUNT_PROFILE_UPDATE_API_ERROR}`,
            { data: this.userForm.value, Response: responseText }
          );
        },
      });
  }

  sendVerificationCode(value: boolean): void {
    this.isLoading = true;
    const username = this.userService.memberProfile.username;
    const email = this.userForm.get('email')?.value;
    this.subscription.add(
      this.memberService.sendOTP(email, username).subscribe(
        (res: string) => {
          this.successMsg = res;
          this.snackBar.openFromTemplate(this.successTemplate, {
            duration: 4000,
            verticalPosition: 'top',
            horizontalPosition: 'center',
          });
          this.otpValue = value ? '' : this.otpValue;
          this.resendOtp = true;
          this.otpVerified = false;
          this.isLoading = false;
        },
        (err) => {
          this.errorMsg = err;
          this.isLoading = false;
          this.snackBar.openFromTemplate(this.errorTemplate, {
            duration: 4000,
            verticalPosition: 'top',
            horizontalPosition: 'center',
          });
        }
      )
    );
  }

  verifyCode(): void {
    this.isLoading = true;
    this.subscription.add(
      this.memberService
        .verifyOTP(Number(this.otpValue), this.userService.memberProfile.username)
        .subscribe(
          (res: string) => {
            this.successMsg = res;
            this.snackBar.openFromTemplate(this.successTemplate, {
              duration: 4000,
              verticalPosition: 'top',
              horizontalPosition: 'center',
            });
            this.otpVerified = true;
            this.emailUpdate = false;
            this.isLoading = false;
          },
          (err) => {
            this.otpVerified = false;
            this.errorMsg = err;
            this.isLoading = false;
            this.snackBar.openFromTemplate(this.errorTemplate, {
              duration: 4000,
              verticalPosition: 'top',
              horizontalPosition: 'center',
            });
          }
        )
    );
  }

  checkEmailUpdateEvent(): void {
    this.emailUpdate = false;
    this.otpVerified = true;
    if (this.userForm.controls['email'].value !== this.userService.memberProfile.email) {
      this.emailUpdate = true;
      this.otpVerified = false;
      this.resendOtp = false;
    }
  }

  checkOtpLength(): boolean {
    return this.otpValue.length > 6 || this.otpValue.length < 6 ? true : false;
  }
  checkIfSpace(number: any): boolean {
    const onlySpace = number.which ? number.which : number.keyCode;
    return onlySpace < 48 || onlySpace > 57 ? false : true;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
